/**
 * The Shuru section's own vocabulary, mapped onto the panel-wide design tokens
 * in component/common/designSystem/theme.js — the same split vidsTheme.js and
 * podsTheme.js use: only domain meaning lives here, palettes belong to the
 * design system.
 */
import { CHART_COLORS, toneChip, toneLookup } from '../../../../component/common/designSystem';

/**
 * Ordered ramps for the distribution charts. Each has exactly as many colours
 * as its band list has bands, so a slice never borrows its neighbour's meaning.
 */

// Chats per person, 1 → 11+. More conversations is deeper engagement, so this
// is a plain shallow → deep ramp.
export const CHAT_DEPTH_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.green
];

// Messages sent on a chat day, 1–2 → 11+. Same direction, same length.
export const MESSAGE_DEPTH_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.green
];

/**
 * The end-of-chat answer: Not great → Same → Better. This is an outcome, not a
 * volume, so it gets the traffic-light reading rather than a brand ramp.
 */
export const FEEDBACK_RAMP = [CHART_COLORS.red, CHART_COLORS.amber, CHART_COLORS.green];

// Message tone, Positive → Negative or neutral. Two states, not a sequence.
export const POSITIVITY_RAMP = [CHART_COLORS.green, CHART_COLORS.gray];

// Text vs voice. Two channels, so two distinct hues rather than a ramp.
export const CHANNEL_RAMP = [CHART_COLORS.brand, CHART_COLORS.teal];

/**
 * The nine moods people pick before opening Shuru, coloured by the `tone` the
 * backend tags each with (see MOODS in services/shuruHubService.js) so the mix
 * reads as "how people arrive" at a glance rather than as nine arbitrary hues.
 */
const MOOD_TONE_COLOR = {
  positive: CHART_COLORS.green,
  neutral: CHART_COLORS.amber,
  negative: CHART_COLORS.pink
};

export const moodColors = (moods = []) =>
  moods.map((mood) => MOOD_TONE_COLOR[mood.tone] || CHART_COLORS.gray);

// Keys match the labels the backend emits (FEEDBACK_LABELS in
// services/shuruHubService.js), so a chip never has to re-derive them.
const feedbackTone = toneLookup({ better: 'success', same: 'warning', 'not great': 'danger' });

export const feedbackChipClass = (label) => toneChip(feedbackTone(label));

const moodTone = toneLookup({ positive: 'success', neutral: 'warning', negative: 'accent' });

export const moodChipClass = (tone) => toneChip(moodTone(tone));
